import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '~/utils/cn';

type Tone = 'info' | 'warning' | 'error' | 'success';

const TONES: Record<Tone, { box: string; icon: string; Icon: typeof Info }> = {
  info: { box: 'bg-surface-blue border-primary/15', icon: 'text-primary', Icon: Info },
  warning: { box: 'bg-warning-50 border-transparent', icon: 'text-warning', Icon: TriangleAlert },
  error: { box: 'bg-error-50 border-transparent', icon: 'text-error', Icon: CircleAlert },
  success: { box: 'bg-success-50 border-transparent', icon: 'text-success', Icon: CircleCheck },
};

/** Inline note used across the transformer screens (guards, checks, resolution notes). */
export function TransformerCallout({
  tone = 'info',
  children,
  className,
  testId = 'transformer-callout',
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  /** E2E `data-testid` for the callout. */
  testId?: string;
}) {
  const { box, icon, Icon } = TONES[tone];
  return (
    <div
      data-testid={testId}
      role={tone === 'error' ? 'alert' : undefined}
      className={cn('flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm', box, className)}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 h-4 w-4 shrink-0', icon)} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
